import {
  CalendarClock,
  CalendarDays,
  CalendarPlus,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Link2,
  List,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { PageHeader } from "@/client/components/page-header";
import { Badge } from "@/client/components/ui/badge";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import { Skeleton } from "@/client/components/ui/skeleton";
import { GuideLink } from "@/client/features/docs/placements";
import { type Hold, holdsApi } from "@/client/features/maintenance/holds-api";
import { HoldsCard } from "@/client/features/maintenance/holds-section";
import {
  type MaintenanceWindow,
  maintenanceApi,
} from "@/client/features/maintenance/maintenance-api";
import { isUpcoming } from "@/client/features/maintenance/upcoming";
import { formatDateTime, formatRelativeTime } from "@/client/lib/format";
import { cn } from "@/client/lib/utils";

/** An active hold that hasn't fully elapsed - worth surfacing as protected time. */
function isHoldUpcoming(h: Hold, now: number): boolean {
  return h.status === "active" && new Date(h.endsAt).getTime() > now;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function ymd(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** Local-date keys (inclusive) a [start, end] span covers, for grid chips. */
function coveredDaysOf(startsAt: string, endsAt: string | null): Set<string> {
  const start = new Date(startsAt);
  const end = endsAt ? new Date(endsAt) : start;
  const days = new Set<string>();
  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  // Cap the span so a malformed range can't loop unbounded.
  for (let i = 0; cursor <= last && i < 366; i++) {
    days.add(ymd(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function chipClass(w: MaintenanceWindow): string {
  if (w.status === "cancelled") {
    return "bg-muted text-muted-foreground line-through";
  }
  if (w.kind === "emergency") {
    return "bg-[var(--danger-bg)] text-[var(--danger-fg)]";
  }
  return "bg-[var(--info-bg)] text-[var(--info-fg)]";
}

function statusBadge(w: MaintenanceWindow) {
  const variant =
    w.status === "in_progress"
      ? "default"
      : w.status === "cancelled"
        ? "outline"
        : w.status === "completed"
          ? "secondary"
          : "secondary";
  const label =
    w.status === "in_progress"
      ? "In progress"
      : w.status === "completed"
        ? "Completed"
        : w.status === "cancelled"
          ? "Cancelled"
          : "Scheduled";
  return <Badge variant={variant}>{label}</Badge>;
}

interface DayChip {
  id: string;
  title: string;
  className: string;
}

function MonthGrid({
  month,
  windows,
  holds,
}: {
  month: Date;
  windows: MaintenanceWindow[];
  holds: Hold[];
}) {
  const year = month.getFullYear();
  const m = month.getMonth();
  const firstWeekday = new Date(year, m, 1).getDay();
  const daysInMonth = new Date(year, m + 1, 0).getDate();

  const byDay = useMemo(() => {
    const map = new Map<string, DayChip[]>();
    const add = (key: string, chip: DayChip) => {
      const list = map.get(key) ?? [];
      list.push(chip);
      map.set(key, list);
    };
    for (const w of windows) {
      for (const key of coveredDaysOf(w.startsAt, w.endsAt)) {
        add(key, { id: `w-${w.id}`, title: w.title, className: chipClass(w) });
      }
    }
    for (const h of holds) {
      if (h.status !== "active") continue;
      for (const key of coveredDaysOf(h.startsAt, h.endsAt)) {
        add(key, {
          id: `h-${h.id}`,
          title: `Hold: ${h.title}`,
          className: "bg-[var(--success-bg)] text-[var(--success-fg)]",
        });
      }
    }
    return map;
  }, [windows, holds]);

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const todayKey = ymd(new Date());

  return (
    <div className="overflow-hidden rounded-lg border">
      <div className="bg-muted/40 grid grid-cols-7 border-b text-center">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-muted-foreground py-2 text-xs font-medium">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((day, i) => {
          if (day === null) {
            return (
              <div key={i} className="bg-muted/20 min-h-24 border-r border-b last:border-r-0" />
            );
          }
          const key = `${year}-${m}-${day}`;
          const events = byDay.get(key) ?? [];
          const isToday = key === todayKey;
          return (
            <div key={i} className="min-h-24 border-r border-b p-1 last:border-r-0">
              <div
                className={cn(
                  "mb-1 flex size-6 items-center justify-center rounded-full text-xs",
                  isToday
                    ? "bg-primary text-primary-foreground font-semibold"
                    : "text-muted-foreground",
                )}
              >
                {day}
              </div>
              <div className="flex flex-col gap-1">
                {events.map((chip) => (
                  <div
                    key={chip.id}
                    title={chip.title}
                    className={cn("truncate rounded px-1.5 py-0.5 text-[11px]", chip.className)}
                  >
                    {chip.title}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EventList({ windows }: { windows: MaintenanceWindow[] }) {
  const sorted = [...windows].sort(
    (a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime(),
  );
  if (sorted.length === 0) {
    return <p className="text-muted-foreground text-sm">No maintenance windows.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-muted-foreground border-b text-left">
            <th className="py-2 pr-4 font-medium">When</th>
            <th className="py-2 pr-4 font-medium">Type</th>
            <th className="py-2 pr-4 font-medium">Title</th>
            <th className="py-2 pr-4 font-medium">Status</th>
            <th className="py-2 text-right font-medium">Calendar</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((w) => (
            <tr key={w.id} className="border-b last:border-0 align-top">
              <td className="text-muted-foreground py-2 pr-4 whitespace-nowrap">
                {formatDateTime(w.startsAt)}
              </td>
              <td className="py-2 pr-4">
                <Badge variant={w.kind === "emergency" ? "destructive" : "outline"}>
                  {w.kind === "emergency" ? "Emergency" : "Scheduled"}
                </Badge>
              </td>
              <td className="py-2 pr-4">
                <div className="font-medium">{w.title}</div>
                {w.description ? (
                  <div className="text-muted-foreground text-xs">{w.description}</div>
                ) : null}
              </td>
              <td className="py-2 pr-4">{statusBadge(w)}</td>
              <td className="py-2 text-right whitespace-nowrap">
                <Button asChild variant="ghost" size="sm">
                  <a href={maintenanceApi.windowIcsUrl(w.id)} download>
                    <CalendarPlus className="size-3.5" />
                    Add to calendar
                  </a>
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type UpcomingItem =
  | { type: "window"; start: number; window: MaintenanceWindow }
  | { type: "hold"; start: number; hold: Hold };

/** The events-first surface: active/future windows and holds, soonest first. */
function UpcomingList({ windows, holds }: { windows: MaintenanceWindow[]; holds: Hold[] }) {
  const now = Date.now();
  const items: UpcomingItem[] = [
    ...windows
      .filter((w) => isUpcoming(w, now))
      .map<UpcomingItem>((w) => ({
        type: "window",
        start: new Date(w.startsAt).getTime(),
        window: w,
      })),
    ...holds
      .filter((h) => isHoldUpcoming(h, now))
      .map<UpcomingItem>((h) => ({ type: "hold", start: new Date(h.startsAt).getTime(), hold: h })),
  ].sort((a, b) => a.start - b.start);

  if (items.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        Nothing upcoming. We'll post maintenance here when something is planned.
      </p>
    );
  }

  return (
    <ul className="flex flex-col divide-y">
      {items.map((item) => {
        // Relative phrasing ("Starts in 3 days") only reads right for the
        // future; active items show the absolute start instead of "ago".
        const future = item.start > now;
        const startsAt = item.type === "window" ? item.window.startsAt : item.hold.startsAt;
        const when = future
          ? `Starts ${formatRelativeTime(startsAt)} · ${formatDateTime(startsAt)}`
          : formatDateTime(startsAt);

        if (item.type === "hold") {
          const h = item.hold;
          return (
            <li
              key={`h-${h.id}`}
              className="flex items-start justify-between gap-3 py-3 first:pt-0"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate text-sm font-medium">{h.title}</p>
                  <Badge variant="secondary">Your hold</Badge>
                </div>
                <p className="text-muted-foreground mt-0.5 text-xs">{when}</p>
                {h.affectedScope ? (
                  <p className="text-muted-foreground mt-1 text-xs">Scope: {h.affectedScope}</p>
                ) : null}
              </div>
            </li>
          );
        }

        const w = item.window;
        return (
          <li key={`w-${w.id}`} className="flex items-start justify-between gap-3 py-3 first:pt-0">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="truncate text-sm font-medium">{w.title}</p>
                {w.kind === "emergency" ? (
                  <Badge variant="destructive">Emergency</Badge>
                ) : (
                  statusBadge(w)
                )}
              </div>
              <p className="text-muted-foreground mt-0.5 text-xs">{when}</p>
              {w.description ? (
                <p className="text-muted-foreground mt-1 text-xs">{w.description}</p>
              ) : null}
            </div>
            <Button asChild variant="ghost" size="sm" className="shrink-0">
              <a href={maintenanceApi.windowIcsUrl(w.id)} download>
                <CalendarPlus className="size-3.5" />
                Add to calendar
              </a>
            </Button>
          </li>
        );
      })}
    </ul>
  );
}

export function CalendarPage() {
  const [windows, setWindows] = useState<MaintenanceWindow[] | null>(null);
  const [holds, setHolds] = useState<Hold[] | null>(null);
  const [icalUrl, setIcalUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [holdsError, setHoldsError] = useState(false);
  const [view, setView] = useState<"list" | "calendar">("list");
  const [showAll, setShowAll] = useState(false);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  // Guards async setState against a unmount mid-flight, for both the initial
  // load and post-mutation refreshes triggered by the holds card.
  const mountedRef = useRef(true);

  const refreshHolds = useCallback(async () => {
    try {
      const res = await holdsApi.list();
      if (!mountedRef.current) return;
      setHolds(res.holds);
      setHoldsError(false);
    } catch {
      if (mountedRef.current) {
        setHoldsError(true);
        toast.error("Couldn't refresh your holds.");
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      try {
        const res = await maintenanceApi.list();
        if (mountedRef.current) {
          setWindows(res.windows);
          setIcalUrl(res.icalUrl);
          setError(false);
        }
      } catch {
        if (mountedRef.current) setError(true);
      }
    })();
    void refreshHolds();
    return () => {
      mountedRef.current = false;
    };
  }, [refreshHolds]);

  const monthLabel = month.toLocaleString(undefined, { month: "long", year: "numeric" });

  const copyIcal = async () => {
    if (!icalUrl) return;
    try {
      await navigator.clipboard.writeText(icalUrl);
      toast.success("Calendar feed URL copied. Subscribe to it in Google or Outlook Calendar.");
    } catch {
      toast.error("Couldn't copy the URL.");
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Maintenance"
        description="Scheduled and emergency maintenance affecting your environment."
      >
        <GuideLink slot="maintenance.help" />
      </PageHeader>

      {/* The view toggle stays available even if the maintenance fetch fails, so
          the holds card below it (its own request) is always reachable. */}
      <div className="flex items-center justify-between gap-2">
        <div className="bg-muted inline-flex rounded-md p-0.5">
          <Button
            size="sm"
            variant={view === "list" ? "secondary" : "ghost"}
            aria-pressed={view === "list"}
            onClick={() => setView("list")}
          >
            <List className="size-3.5" />
            List
          </Button>
          <Button
            size="sm"
            variant={view === "calendar" ? "secondary" : "ghost"}
            aria-pressed={view === "calendar"}
            onClick={() => setView("calendar")}
          >
            <CalendarDays className="size-3.5" />
            Calendar
          </Button>
        </div>
        {icalUrl ? (
          <Button variant="outline" size="sm" onClick={copyIcal}>
            <Link2 className="size-3.5" />
            Subscribe (iCal)
          </Button>
        ) : null}
      </div>

      {view === "list" ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <CalendarClock className="text-muted-foreground size-4" />
                Upcoming
              </CardTitle>
              <CardDescription>
                Maintenance and your no-disruption holds, soonest first.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error ? (
                <p className="text-muted-foreground text-sm">
                  Couldn't load maintenance. Refresh to try again. Your holds are shown below.
                </p>
              ) : windows === null ? (
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                </div>
              ) : (
                <UpcomingList windows={windows} holds={holds ?? []} />
              )}
            </CardContent>
          </Card>

          <HoldsCard holds={holds} error={holdsError} onChanged={refreshHolds} />

          <Card>
            <CardContent className="pt-6">
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                aria-expanded={showAll}
                className="text-muted-foreground hover:text-foreground flex w-full items-center justify-between gap-2 text-xs font-medium tracking-wide uppercase"
              >
                <span className="flex items-center gap-2">
                  <CalendarClock className="size-3.5" />
                  All events
                </span>
                <ChevronDown
                  className={cn("size-4 transition-transform", showAll && "rotate-180")}
                />
              </button>
              {showAll ? (
                <div className="mt-4">
                  {error ? (
                    <p className="text-muted-foreground text-sm">
                      Couldn't load maintenance. Refresh to try again.
                    </p>
                  ) : windows === null ? (
                    <Skeleton className="h-24 w-full" />
                  ) : (
                    <EventList windows={windows} />
                  )}
                </div>
              ) : null}
            </CardContent>
          </Card>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              aria-label="Previous month"
              onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <span className="min-w-40 text-center text-sm font-medium">{monthLabel}</span>
            <Button
              variant="outline"
              size="icon"
              aria-label="Next month"
              onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>

          {error ? (
            <Card>
              <CardContent className="text-muted-foreground py-12 text-center text-sm">
                Couldn't load maintenance. Refresh to try again.
              </CardContent>
            </Card>
          ) : windows === null ? (
            <Skeleton className="h-96 w-full" />
          ) : (
            <MonthGrid month={month} windows={windows} holds={holds ?? []} />
          )}

          {/* Holds remain manageable from the calendar view too. */}
          <HoldsCard holds={holds} error={holdsError} onChanged={refreshHolds} />
        </>
      )}
    </div>
  );
}
